export class RequestTimeoutError extends Error {
  constructor() {
    super("The request took too long. Check your connection and try again.");
    this.name = "RequestTimeoutError";
  }
}

/** Bound the entire read, including response decoding and an auth refresh wait. */
export async function withRequestTimeout<T>(
  request: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  parentSignal?: AbortSignal | null,
): Promise<T> {
  const controller = new AbortController();
  const abortFromParent = () => controller.abort(parentSignal?.reason);
  if (parentSignal?.aborted) abortFromParent();
  else parentSignal?.addEventListener("abort", abortFromParent, { once: true });

  const timer = setTimeout(() => controller.abort(new RequestTimeoutError()), timeoutMs);
  let onAbort: () => void = () => undefined;
  try {
    const aborted = new Promise<never>((_, reject) => {
      onAbort = () => reject(controller.signal.reason ?? new DOMException("Aborted", "AbortError"));
      if (controller.signal.aborted) onAbort();
      else controller.signal.addEventListener("abort", onAbort, { once: true });
    });
    // A suspended refresh/fetch must not keep the page's spinner alive even if
    // the underlying operation is slow to observe the cancellation signal.
    return await Promise.race([
      aborted,
      controller.signal.aborted ? aborted : request(controller.signal),
    ]);
  } finally {
    clearTimeout(timer);
    controller.signal.removeEventListener("abort", onAbort);
    parentSignal?.removeEventListener("abort", abortFromParent);
  }
}
